import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Instagram, Facebook, Twitter } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";
import TrustBar from "../components/TrustBar";

const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/9e1799fe-eec8-454a-a2a5-049eeb63831e/images/d70f9ea3d9298b39950ee2ce459aa83854f0f8763cedf29cda087d8e7459b1c9.jpeg";

export function About() {
  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <SEO title="About Us" path="/about" description="Queen MJ Maison Beaute is a premium fragrance destination focused on elegant perfumes and beautiful home fragrances — composed for those who wear elegance as a second skin." />
      <div className="relative h-[50vh] min-h-[360px] overflow-hidden">
        <img src={ABOUT_IMG} alt="Queen MJ Maison Beaute luxury fragrance store" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-full flex flex-col justify-end pb-14">
          <p className="eyebrow mb-3">OUR STORY</p>
          <h1 className="font-serif-lux text-5xl sm:text-6xl font-light text-[#FAFAF7]">A Premium Fragrance <span className="gold-text italic">Destination</span></h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-20 space-y-8 text-[#FAFAF7]/70 font-light leading-relaxed">
        <p className="text-xl font-serif-lux text-[#FAFAF7] italic">Queen MJ Maison Beaute was born from a singular belief — that fragrance is the most intimate form of luxury.</p>
        <p>We are a premium fragrance destination devoted to two things: elegant perfumes that become your signature, and beautiful home fragrances that turn a house into a sanctuary. Every piece in our collection is chosen for its character, its craftsmanship, and its ability to make an ordinary moment feel extraordinary.</p>
        <p>Our perfumes are composed with the finest raw materials — luminous saffron, precious oud, May rose and warm amber — and presented in bottles worthy of the scent within. Our candles and air fresheners are hand-finished to carry that same sense of occasion into your home.</p>
        <p>We believe luxury should feel personal, not intimidating. That's why every order arrives beautifully packaged with complimentary samples, backed by our authenticity guarantee, fast shipping and easy returns. This is fragrance, elevated.</p>
        <div className="grid grid-cols-3 gap-6 pt-8">
          {[["100%", "Authentic Guarantee"], ["24h", "Order Dispatch"], ["150k+", "Devoted Clients"]].map(([n, l]) => (
            <div key={l} className="text-center gold-border rounded-xl py-6 bg-[#141416]">
              <p className="font-serif-lux text-4xl gold-text">{n}</p>
              <p className="eyebrow text-[0.55rem] mt-2">{l}</p>
            </div>
          ))}
        </div>
        <div className="text-center pt-6">
          <Link to="/shop" className="btn-gold px-8 py-3.5 rounded-full text-sm inline-block">Explore the Collection</Link>
        </div>
      </div>
      <TrustBar />
    </div>
  );
}

const FAQS = [
  ["Are your fragrances authentic?", "Absolutely — every product is 100% genuine, backed by our authenticity guarantee."],
  ["How fast is shipping?", "Orders dispatch within 24 hours. Standard delivery is free over $150; express arrives in 1–2 days."],
  ["What's your returns policy?", "Unopened items can be returned within 30 days for a full refund."],
];

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — our customer care team will respond shortly");
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error("Could not send message"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <SEO title="Contact" path="/contact" description="Contact Queen MJ Maison Beaute customer care — email, phone, business hours and social. We're here 7 days a week." />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <p className="eyebrow mb-3">GET IN TOUCH</p>
        <h1 className="font-serif-lux text-5xl font-light text-[#FAFAF7] mb-12">Contact the Maison</h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            {[[MapPin, "Address", "Encore Court, Jacksonville, FL 32205, United States"], [Mail, "Email", "support@queenmjmaison.store"], [Phone, "Telephone", "+1 (904) 219-6276"], [Clock, "Business Hours", "Mon–Sat · 10am – 7pm EST · Sun 11am – 5pm"]].map(([Icon, t, v]) => (
              <div key={t} className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-full gold-border flex items-center justify-center shrink-0"><Icon size={18} className="text-[#D4AF37]" /></div>
                <div><p className="eyebrow text-[0.6rem] mb-1">{t}</p><p className="text-[#FAFAF7]">{v}</p></div>
              </div>
            ))}
            <div className="pt-2">
              <p className="eyebrow text-[0.6rem] mb-3">Follow Us</p>
              <div className="flex gap-4">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-11 h-11 rounded-full gold-border flex items-center justify-center text-[#FAFAF7]/70 hover:text-[#D4AF37]"><Instagram size={18} /></a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-11 h-11 rounded-full gold-border flex items-center justify-center text-[#FAFAF7]/70 hover:text-[#D4AF37]"><Facebook size={18} /></a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="w-11 h-11 rounded-full gold-border flex items-center justify-center text-[#FAFAF7]/70 hover:text-[#D4AF37]"><Twitter size={18} /></a>
              </div>
            </div>
          </div>
          <form onSubmit={submit} className="gold-border rounded-xl bg-[#141416] p-8 space-y-4">
            <input data-testid="contact-name" required placeholder="Your name" value={form.name} onChange={set("name")} className="input-lux w-full" />
            <input data-testid="contact-email" type="email" required placeholder="Your email" value={form.email} onChange={set("email")} className="input-lux w-full" />
            <textarea data-testid="contact-message" required rows={5} placeholder="How may we assist you?" value={form.message} onChange={set("message")} className="input-lux w-full resize-none" />
            <button data-testid="contact-submit" disabled={busy} className="btn-gold w-full py-3.5 rounded-full text-sm disabled:opacity-60">{busy ? "Sending…" : "Send Message"}</button>
          </form>
        </div>

        <div className="mt-12 gold-border rounded-xl overflow-hidden" data-testid="contact-map">
          <iframe
            title="Queen MJ Maison Beaute — Jacksonville, FL"
            src="https://www.google.com/maps?q=Encore+Court,+Jacksonville,+FL+32205&output=embed"
            className="w-full h-72 sm:h-96 grayscale-[35%] contrast-[1.05]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>

        <div className="mt-16">
          <p className="eyebrow mb-6">QUICK ANSWERS</p>
          <div className="grid sm:grid-cols-3 gap-5">
            {FAQS.map(([q, a]) => (
              <div key={q} className="gold-border rounded-xl bg-[#141416] p-5">
                <p className="font-serif-lux text-lg text-[#FAFAF7] mb-2">{q}</p>
                <p className="text-sm text-[#FAFAF7]/55 font-light leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/faq" className="text-sm text-[#D4AF37] link-underline">View all FAQs</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
